import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import CallbackModal from '../../components/CallbackModal'
import CalComModal from '../../components/CalComModal'
import FAQAccordion from '../../components/FAQAccordion'

type Locale = 'tr' | 'en' | 'ru'

export interface LocationPageContent {
  locale: Locale
  slug: string
  meta: {
    title: string
    description: string
    keywords?: string[]
    canonical?: string
    image?: string
  }
  hero: {
    badge: string
    title: string
    subtitle: string
    stats?: {
      label: string
      value: string
    }[]
    highlights?: {
      title: string
      description: string
    }[]
  }
  problemSolution: {
    title: string
    subtitle?: string
    items: {
      title: string
      description: string
    }[]
  }
  services: {
    title: string
    subtitle?: string
    items: {
      title: string
      description: string
      icon?: string
    }[]
  }
  locationBenefits: {
    title: string
    subtitle?: string
    items: {
      title: string
      description: string
    }[]
  }
  industries?: {
    title: string
    subtitle?: string
    items: {
      title: string
      description: string
    }[]
  }
  process?: {
    title: string
    subtitle?: string
    items: {
      title: string
      description: string
    }[]
  }
  testimonials?: {
    quote: string
    author: {
      name: string
      title: string
    }
  }[]
  faqs: {
    question: string
    answer: string
  }[]
  cta: {
    badge?: string
    title: string
    description: string
  }
  areaServed: string[]
  coordinates: {
    latitude: number
    longitude: number
  }
  address: {
    streetAddress: string
    addressLocality: string
    addressRegion: string
    postalCode: string
    addressCountry: string
  }
  relatedServices?: {
    title: string
    items: {
      name: string
      href: string
      description?: string
    }[]
  }
}

interface LocationLandingLayoutProps {
  content: LocationPageContent
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

const containerStagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
}

export const LocationLandingLayout = ({ content }: LocationLandingLayoutProps) => {
  const [isCalModalOpen, setIsCalModalOpen] = useState(false)
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false)

  const canonicalUrl = useMemo(() => {
    if (content.meta.canonical) return content.meta.canonical
    return `https://thinklyze.com/${content.slug}`
  }, [content.meta.canonical, content.slug])

  const businessSchema = useMemo(() => {
    const { meta, coordinates, address, areaServed } = content

    return {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      '@id': `${canonicalUrl}#professional-service`,
      name: meta.title,
      url: canonicalUrl,
      description: meta.description,
      areaServed,
      serviceType: content.hero.title,
      telephone: '+90 553 657 64 54',
      image: meta.image || 'https://thinklyze.com/images/logo_siyah.webp',
      provider: {
        '@type': 'Organization',
        name: 'Thinklyze',
        url: 'https://thinklyze.com',
        logo: 'https://thinklyze.com/images/logo_siyah.webp',
        sameAs: [
          'https://www.instagram.com/thinklyze/',
          'https://www.linkedin.com/company/thinklyze',
          'https://www.facebook.com/thinklyze',
          'https://www.youtube.com/@thinklyze'
        ],
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      },
      address: {
        '@type': 'PostalAddress',
        ...address,
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Dijital Pazarlama ve Web Hizmetleri',
        itemListElement: content.services.items.map((item) => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: item.title,
            description: item.description,
            areaServed,
          },
        })),
      },
      knowsAbout: [
        content.hero.title,
        ...content.services.items.map((item) => item.title),
        ...(content.locationBenefits.items?.map((item) => item.title) || []),
      ],
      sameAs: [
        'https://www.instagram.com/thinklyze/',
        'https://www.linkedin.com/company/thinklyze',
        'https://www.facebook.com/thinklyze',
        'https://www.youtube.com/@thinklyze'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+90 553 657 64 54',
        contactType: 'customer service',
        availableLanguage: ['Turkish', 'English', 'Russian'],
      },
      mainEntityOfPage: canonicalUrl,
    }
  }, [canonicalUrl, content])

  const servicesSchema = useMemo(() => {
    return content.services.items.map((item) => ({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: item.title,
      description: item.description,
      serviceType: item.title,
      provider: {
        '@type': 'Organization',
        name: 'Thinklyze',
        url: 'https://thinklyze.com',
      },
      areaServed: content.areaServed,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'PriceSpecification',
          priceCurrency: 'TRY',
        },
      },
    }))
  }, [content.areaServed, content.services.items])

  const breadcrumbSchema = useMemo(() => {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Ana Sayfa',
          item: 'https://thinklyze.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Hizmetler',
          item: 'https://thinklyze.com/services',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: content.hero.title,
          item: canonicalUrl,
        },
      ],
    }
  }, [canonicalUrl, content.hero.title])

  const webPageSchema = useMemo(() => {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: content.meta.title,
      url: canonicalUrl,
      description: content.meta.description,
      inLanguage: content.locale === 'tr' ? 'tr-TR' : content.locale === 'en' ? 'en-US' : 'ru-RU',
      breadcrumb: {
        '@id': `${canonicalUrl}#breadcrumb`,
      },
      primaryImageOfPage: content.meta.image || 'https://thinklyze.com/images/logo_siyah.webp',
      about: content.services.items.map((item) => item.title),
      isPartOf: {
        '@type': 'WebSite',
        url: 'https://thinklyze.com',
        name: 'Thinklyze',
      },
    }
  }, [canonicalUrl, content.locale, content.meta.description, content.meta.image, content.meta.title, content.services.items])

  const faqSchema = useMemo(() => {
    if (!content.faqs.length) return null

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    }
  }, [content.faqs])

  const handlePrimaryCTA = () => {
    setIsCalModalOpen(true)
  }

  const handleSecondaryCTA = () => {
    setIsCallbackModalOpen(true)
  }

  return (
    <>
      <Helmet>
        <title>{content.meta.title}</title>
        <meta name="description" content={content.meta.description} />
        {content.meta.keywords && <meta name="keywords" content={content.meta.keywords.join(', ')} />}
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(businessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        {servicesSchema.map((schema, index) => (
          <script key={`service-schema-${index}`} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <section className="relative pt-28 pb-20 overflow-hidden bg-[#f9f9f9]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerStagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block text-[13px] font-medium text-[#666] bg-white px-4 py-1.5 rounded-full border border-gray-200 shadow-sm"
            >
              {content.hero.badge}
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="text-[28px] md:text-[44px] font-bold text-[#111] leading-tight mt-6"
            >
              {content.hero.title}
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="mt-6 text-lg md:text-xl text-[#555] leading-relaxed"
            >
              {content.hero.subtitle}
            </motion.p>
          </motion.div>

          {content.hero.highlights && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={containerStagger}
              className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {content.hero.highlights.map((highlight, idx) => (
                <motion.div
                  key={highlight.title}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{highlight.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{highlight.description}</p>
                </motion.div>
              ))}
            </motion.div>
          )}

          {content.hero.stats && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={containerStagger}
              className="mt-14 grid gap-6 sm:grid-cols-3"
            >
              {content.hero.stats.map((stat) => (
                <motion.div
                  key={stat.label}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl p-6 border border-gray-200 flex flex-col items-center text-center"
                >
                  <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
                  <span className="mt-2 text-sm text-gray-500">{stat.label}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA — Hero altı */}
      <section className="relative py-20 bg-[#0B1220] text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] bg-blue-500/20 blur-[140px] rounded-full" />
          <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/60 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.12),transparent_55%)]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          {content.cta.badge && (
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 text-[13px] font-semibold tracking-wide uppercase text-white/80 border border-white/20 shadow-[0_10px_40px_rgba(15,23,42,0.25)]">
              {content.cta.badge}
            </span>
          )}
          <h2 className="text-[clamp(30px,4.5vw,46px)] font-bold mt-7 leading-tight">
            {content.cta.title}
          </h2>
          <p className="mt-6 text-blue-100/90 text-lg leading-relaxed max-w-3xl mx-auto">
            {content.cta.description}
          </p>

          {content.areaServed?.length > 0 && (
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {content.areaServed.map((city) => (
                <span
                  key={city}
                  className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm text-blue-100/90 backdrop-blur-sm"
                >
                  {city}
                </span>
              ))}
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handlePrimaryCTA}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#0B1220] font-semibold text-sm shadow-[0_20px_50px_rgba(39,94,255,0.25)] hover:shadow-[0_16px_40px_rgba(39,94,255,0.35)] hover:-translate-y-0.5 transition-all duration-300"
            >
              Toplantı Planlayalım
            </button>
            <button
              onClick={handleSecondaryCTA}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/10 border border-white/20 font-semibold text-sm text-white hover:bg-white/15 hover:border-white/40 transition-all duration-300 backdrop-blur-md"
            >
              Sizi Arayalım
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
            variants={containerStagger}
            className="grid gap-8 md:grid-cols-5"
          >
            <motion.div variants={fadeInUp} className="md:col-span-2">
              <h2 className="text-[clamp(28px,4vw,36px)] font-bold text-gray-900">
                {content.problemSolution.title}
              </h2>
              {content.problemSolution.subtitle && (
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {content.problemSolution.subtitle}
                </p>
              )}
            </motion.div>
            <motion.div variants={fadeInUp} className="md:col-span-3 grid gap-6">
              {content.problemSolution.items.map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-2xl border border-gray-200 bg-[#F9FAFB] hover:bg-white hover:border-gray-300 transition-all duration-300"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F7F8FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <div>
              <h2 className="text-[clamp(28px,4vw,36px)] font-bold text-gray-900">
                {content.services.title}
              </h2>
              {content.services.subtitle && (
                <p className="mt-3 text-gray-600">{content.services.subtitle}</p>
              )}
            </div>
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
            variants={containerStagger}
            className="grid gap-6 md:grid-cols-2"
          >
            {content.services.items.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-[clamp(28px,4vw,36px)] font-bold text-gray-900">
              {content.locationBenefits.title}
            </h2>
            {content.locationBenefits.subtitle && (
              <p className="mt-4 text-gray-600 leading-relaxed">
                {content.locationBenefits.subtitle}
              </p>
            )}
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
            variants={containerStagger}
            className="mt-12 grid gap-6 md:grid-cols-3"
          >
            {content.locationBenefits.items.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                className="p-6 rounded-2xl border border-gray-200 bg-[#F9FAFB] hover:border-gray-300 hover:bg-white transition-all duration-300"
              >
                <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {content.industries && (
        <section className="py-16 md:py-20 bg-[#0F172A] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={fadeInUp}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-[clamp(28px,4vw,36px)] font-bold">{content.industries.title}</h2>
              {content.industries.subtitle && (
                <p className="mt-4 text-blue-100 leading-relaxed">
                  {content.industries.subtitle}
                </p>
              )}
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={containerStagger}
              className="mt-12 grid gap-6 md:grid-cols-3"
            >
              {content.industries.items.map((item) => (
                <motion.div
                  key={item.title}
                  variants={fadeInUp}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                >
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-3 text-sm text-blue-100 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {content.process && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={fadeInUp}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-[clamp(28px,4vw,36px)] font-bold text-gray-900">
                {content.process.title}
              </h2>
              {content.process.subtitle && (
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {content.process.subtitle}
                </p>
              )}
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={containerStagger}
              className="mt-12 grid gap-6 md:grid-cols-4"
            >
              {content.process.items.map((item, index) => (
                <motion.div
                  key={item.title}
                  variants={fadeInUp}
                  className="relative bg-[#F9FAFB] border border-gray-200 rounded-2xl p-6 hover:bg-white hover:border-gray-300 transition-all duration-300"
                >
                  <span className="absolute -top-5 left-6 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0F172A] text-white text-sm font-semibold shadow-lg">
                    {index + 1}
                  </span>
                  <h3 className="mt-6 text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {content.relatedServices && (
        <section className="py-16 md:py-20 bg-[#F7F8FB]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={fadeInUp}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-[clamp(28px,4vw,36px)] font-bold text-gray-900">
                {content.relatedServices.title}
              </h2>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0 }}
              variants={containerStagger}
              className="mt-12 grid gap-6 md:grid-cols-3"
            >
              {content.relatedServices.items.map((item) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  variants={fadeInUp}
                  className="block bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                  {item.description && (
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.description}</p>
                  )}
                </motion.a>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion
            title="SSS"
            subtitle="İstanbul'daki işletmelerin en çok merak ettiği soruları cevapladık."
            faqs={content.faqs}
            onCTAClick={handlePrimaryCTA}
            ctaText="Toplantı Planlayalım"
          />
        </div>
      </section>

      <CalComModal isOpen={isCalModalOpen} onClose={() => setIsCalModalOpen(false)} />
      <CallbackModal isOpen={isCallbackModalOpen} onClose={() => setIsCallbackModalOpen(false)} />
    </>
  )
}

export default LocationLandingLayout















